import React, { useEffect, useState } from 'react';
import { useUpdateAccountMutation } from '../endpoints/accounts.endpoints';
import { Account } from '../types/client-management.types';
import { CircleAlert } from 'lucide-react';
import CompanySelector from './company-selector';
import { Company } from 'src/pages/billing/types/billing.types';

interface AccountTableProps {
  accounts: Account[];
  companies: Company[];
  filter?: 'attention_needed' | 'all';
  className?: string;
  searchQuery: string;
  onCompanySelect?: (accountId: string, companyId: string) => void;
}

const AccountTable: React.FC<AccountTableProps> = ({
  className,
  filter = 'all',
  accounts,
  companies,
  searchQuery,
  onCompanySelect,
}) => {
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);

  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation();

  useEffect(() => {
    setFilteredAccounts(accounts.filter((account: Account) => {
      if (filter === 'attention_needed') {
        if (searchQuery.trim()) {
          return account.attention_needed && (account.title.toLowerCase().includes(searchQuery.toLowerCase()) || account.source.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return account.attention_needed;
      }

      if (searchQuery.trim()) {
        return account.title.toLowerCase().includes(searchQuery.toLowerCase()) || account.source.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    }));
  }, [filter, accounts, searchQuery]);

  const handleCompanySelect = async(accountId: string, companyId: string) => {
    try {
      await updateAccount({
        accountId,
        company: companyId || null,
      }).unwrap();
      onCompanySelect?.(accountId, companyId);
    } catch (error) {
      console.error('Failed to update company:', error);
    }
  };


  return (
    <div className={`overflow-y-scroll ${className} pb-[100px]`}>

      <div className='flex flex-row flex-wrap gap-4'>
        {filteredAccounts.map((account: Account) => (
          <div
            className='bg-white flex flex-col justify-between rounded-lg p-4 w-fit relative'
            key={account.id}
          >
            {account.attention_needed && (
              <div className='absolute top-2 right-2'>
                <button className='text-red-500 bg-white rounded-full opacity-80'>
                  <CircleAlert className='size-4' />
                </button>
              </div>
            )}
            <div className='text-lg font-normal'>
              {account.title}
            </div>
            <div className='text-xs text-gray-500'>
              {account.source.toLocaleLowerCase().replace('_', ' ')}
            </div>

            <CompanySelector
              accountId={account.id}
              companies={companies}
              companyId={account.company || ''}
              accountName={account.title}
              campaignName={account.ext_account_id}
              attentionNeeded={account.attention_needed}
              onCompanySelect={(companyId) => handleCompanySelect(account.id, companyId)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccountTable;
